import AddCircleIcon from '@mui/icons-material/AddCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import React, { useContext, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { transformListData } from '_lib/api/apiResourceFactory';
import {
  activeCallOutcomes,
  AgentTypeEnum,
  CallOutcomeEnum,
  PhoneCallDetailDTO,
  PhoneCallQueryParams,
  TimezoneAbbreviationEnum
} from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { Button } from '_lib/components/Button/Button';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { DebouncedTextField } from '_lib/components/DebouncedTextField/DebouncedTextField';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { GridV2 } from '_lib/components/GridV2/GridV2';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { LinkListItemButton } from '_lib/components/LinkListItemButton/LinkListItemButton';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { MaskedPhoneInput } from '_lib/components/PhoneNumberTextField/PhoneNumberTextField';
import { DatePicker } from '_lib/components/Pickers/DatePicker/DatePicker';
import { Search } from '_lib/components/Search/Search';
import { SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Stack } from '_lib/components/Stack/Stack';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';

import { Typography } from '_lib/components/Typography/Typography';

import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { useCallCustomerDialog } from '_lib/hooks/CallCustomerDialogHook';
import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { normalizers, useQueryParams } from '_lib/hooks/useQueryParams';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import { parsePhone10 } from '_lib/utils/helpers';

import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { SimpleAppBar } from '../../components/AppBar/SimpleAppBar';
import { AppDrawerState, AppRouteContext } from '../App/app.types';

import { AuthContext } from '../auth/authContext';
import { CallCenterAdmin, CallCenterUser, SystemUser } from '../auth/permissionHelpers';
import { appRoutePaths, getFullAppRoutePath, getGoBackState } from '../Nav/nav.types';
import { PagedList } from '../PagedList/PagedList';
import { ScrollableContentWrapper } from '../ScrollableContentWrapper/ScrollableContentWrapper';
import { SearchSelect } from '../SearchSelect/SearchSelect';

import { downloadCsv, generatePhoneCallsCsv } from './PhoneCallCsvService';

interface PhoneCallListProps {
  appDrawerState?: AppDrawerState;
  timezone: TimezoneAbbreviationEnum;
}

const agentTypeOptions = [AgentTypeEnum.CallCenter, AgentTypeEnum.Account, 'ALL'] as const;
type AgentTypeFilter = (typeof agentTypeOptions)[number];

enum DateRangeEnum {
  Today = 'Today',
  WeekToDate = 'Week to Date',
  MonthToDate = 'Month to Date',
  YearToDate = 'Year to Date',
  CustomRange = 'Custom Range'
}
export const dateRangeOptions = Object.values(DateRangeEnum);
type DateRangeFilter = (typeof dateRangeOptions)[number];
export const isDateRangeOption = (val: string): val is DateRangeFilter => dateRangeOptions.some((s) => s === val);

const searchSelectSx = {
  marginTop: 1,
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    paddingTop: '3px',
    paddingBottom: '3px'
  },
  '& .MuiFormControl-root.MuiTextField-root': {
    marginY: 0
  }
};

const PhoneCallList = (props: PhoneCallListProps) => {
  const { appDrawerState, timezone } = props;

  const callCustomerDialog = useCallCustomerDialog();
  const alertDialog = useAlertDialog();

  const navigate = useNavigate();
  const snackBar = useOurSnackbar();

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const api = useContext(ApiContext);

  const { authUserCacheResource, callCenterCacheResource } = useContext(AuthContext);

  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const { isAccountUser } = useAuthRoles();

  const [csvLoading, setCsvLoading] = useState(false);

  const companyNameQuery = api.companies.useUnpagedList({
    params: { serializer: 'name', accountId: cachedAuthUserQuery.data?.accountId || undefined }
  });

  const { params, setParam, setParams, deleteParam, clearParams, searchParams } = useQueryParams({
    outcome: { defaultValue: undefined, normalize: normalizers.enum(Object.values(CallOutcomeEnum)) },
    companyId: { defaultValue: '' },
    accountId: { defaultValue: '' },
    agentType: {
      defaultValue: AgentTypeEnum.CallCenter as AgentTypeFilter,
      normalize: normalizers.enum(agentTypeOptions)
    },
    companyDNIS: { defaultValue: '' },
    createdById: { defaultValue: '' },
    callerId: { defaultValue: '', normalize: (value) => parsePhone10(value) || '' },
    searchWords: { defaultValue: '' },
    aiTransfer: { defaultValue: false, normalize: normalizers.boolean },
    dateRangeFilter: { defaultValue: DateRangeEnum.Today, normalize: normalizers.enum(dateRangeOptions) },
    createdAt_gte: { defaultValue: '', normalize: normalizers.isoDate },
    createdAt_lte: { defaultValue: '', normalize: normalizers.isoDate }
  });

  const companyQuery = api.companies.useGetById(params.companyId ? { id: params.companyId } : { skipQuery: true });
  const accountQuery = api.accounts.useGetById(params.accountId ? { id: params.accountId } : { skipQuery: true });

  let sanitizedAgentType: AgentTypeFilter | undefined = undefined;
  if (cachedAuthUserQuery?.data?.accountId) {
    sanitizedAgentType = AgentTypeEnum.Account;
  } else if (
    cachedAuthUserQuery?.data &&
    (SystemUser({ user: cachedAuthUserQuery.data }) || CallCenterAdmin({ user: cachedAuthUserQuery.data }))
  ) {
    sanitizedAgentType = params.agentType;
  } else if (cachedAuthUserQuery?.data) {
    sanitizedAgentType = AgentTypeEnum.CallCenter;
  }

  const accountUserQueryEnabled =
    !!cachedAuthUserQuery.data?.accountId ||
    (!!sanitizedAgentType &&
      ['ALL', AgentTypeEnum.Account].includes(sanitizedAgentType) &&
      sanitizedAgentType !== AgentTypeEnum.CallCenter);

  const callCenterUserQueryEnabled =
    !!cachedAuthUserQuery.data &&
    (SystemUser({ user: cachedAuthUserQuery.data }) || !!cachedAuthUserQuery.data.callCenterId) &&
    !cachedAuthUserQuery.data.accountId &&
    !!sanitizedAgentType &&
    ['ALL', AgentTypeEnum.CallCenter].includes(sanitizedAgentType) &&
    sanitizedAgentType !== AgentTypeEnum.Account;

  const accountUsersQuery = api.accountUsers.useUnpagedList(
    {
      params: {
        accountId: cachedAuthUserQuery.data?.accountId ?? params.accountId
      }
    },
    {
      enabled: accountUserQueryEnabled
    }
  );
  const callCenterUsersQuery = api.users.useUnpagedList(
    { params: { callCenterId: cachedAuthUserQuery.data?.callCenterId || undefined } },
    {
      enabled: callCenterUserQueryEnabled
    }
  );

  const accessibleCallAgents = ((accountUserQueryEnabled && accountUsersQuery.data) || []).concat(
    (callCenterUserQueryEnabled && callCenterUsersQuery.data) || []
  );
  const selectedCallAgent = accessibleCallAgents.find((user) => user.id === params.createdById);

  /**
   * Ignore 'companyDNIS' param if not in current company data
   */
  const sanitizedCompanyDNIS =
    params.companyDNIS &&
    !companyQuery.data?.callCenter.dnisArray.find((dnisItem) => dnisItem.phoneNumber === params.companyDNIS)
      ? ''
      : params.companyDNIS;
  const now = new Date();

  const createdAt_gte_Today = DateUtils.toStartOf(now, 'day', { timezone }).toISOString();
  const createdAt_gte_Week = DateUtils.toStartOf(now, 'week', { timezone }).toISOString();
  const createdAt_gte_Month = DateUtils.toStartOf(now, 'month', { timezone }).toISOString();
  const createdAt_gte_Year = DateUtils.toStartOf(now, 'year', { timezone }).toISOString();

  const createdAt_lte_Today = DateUtils.toEndOf(now, 'day', { timezone }).toISOString();

  const phoneCallParams: PhoneCallQueryParams = {
    callCenterId: cachedCallCenterQuery.data?.id,
    outcome: params.outcome,
    companyId: companyQuery.data?.id,
    accountId: cachedAuthUserQuery?.data?.accountId ?? accountQuery.data?.id,
    agentType: sanitizedAgentType === 'ALL' ? undefined : sanitizedAgentType,
    companyDNIS: sanitizedCompanyDNIS,
    createdById: params.createdById,
    callerId: params.callerId,
    searchWords: params.searchWords,
    aiTransfer: params.aiTransfer,
    createdAt_gte: params.createdAt_gte || createdAt_gte_Today,
    createdAt_lte: params.createdAt_lte || createdAt_lte_Today
  };

  const enablePhoneCallQueries =
    !!cachedAuthUserQuery?.data &&
    !companyQuery.isFetching &&
    !accountQuery.isFetching &&
    sanitizedAgentType !== undefined;

  const phoneCallQuery = api.phoneCallsV1.useList(
    {
      params: phoneCallParams
    },
    { enabled: enablePhoneCallQueries }
  );

  const phoneCallCountQuery = api.phoneCallsV1.useCount(
    { params: phoneCallParams },
    { enabled: enablePhoneCallQueries }
  );

  const phoneCallBookedCountQueryEnabled = !phoneCallParams.outcome && enablePhoneCallQueries;
  const phoneCallBookedCountQuery = api.phoneCallsV1.useCount(
    {
      params: { ...phoneCallParams, outcome: CallOutcomeEnum.NewAppointment }
    },
    { enabled: phoneCallBookedCountQueryEnabled }
  );

  const [callerIdIsPartiallyFilled, setCallerIdIsPartiallyFilled] = useState(false);

  /**
   * When changing accounts, clear out call agent
   */
  const [lastAccountId, setLastAccountId] = useState(params.accountId);
  if (params.accountId && params.accountId !== lastAccountId) {
    setLastAccountId(params.accountId);
    deleteParam('createdById');
  }

  /**
   * When changing agent type, clear out call agent
   */
  const [lastAgentType, setLastAgentType] = useState(params.agentType);
  if (params.agentType && params.agentType !== lastAgentType) {
    setLastAgentType(params.agentType);
    deleteParam('createdById');
  }

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%' }}>
      <SimpleAppBar title='Call Log' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <ActionBar
        actions={
          <>
            <Button
              disableFocusRipple
              text='New Call'
              variant='text'
              onClick={async () => {
                await callCustomerDialog.show({});
              }}
              startIcon={<AddCircleIcon />}
              sx={{ textTransform: 'none' }}
            />
            <Button
              disableFocusRipple
              text='Download CSV'
              variant='text'
              onClick={async () => {
                const fetchAllPhoneCalls = async () => {
                  if (!phoneCallCountQuery.data) {
                    return [];
                  }

                  const totalCountToFetch = phoneCallCountQuery.data;
                  const pageSize = 250;
                  const pagesToFetch = Math.ceil(totalCountToFetch / pageSize);

                  const fetchPage = async (page: number) => {
                    const req = await api.phoneCallsV1.listFull({ params: { ...phoneCallParams, pageSize, page } });
                    return req.results;
                  };

                  const phoneCallData: PhoneCallDetailDTO[] = [];

                  for (let i = 0; i < pagesToFetch; i++) {
                    const pageData = await fetchPage(i + 1);
                    phoneCallData.push(...pageData);
                  }

                  return phoneCallData;
                };

                if (isAccountUser) {
                  const startDate = params.createdAt_gte;
                  const endDate = params.createdAt_lte;

                  const threeMonthsAfterStartDate = DateUtils.add(new Date(startDate), { months: 3 });

                  if (DateUtils.isAfterOrEqual(endDate, threeMonthsAfterStartDate)) {
                    await alertDialog.show({
                      variant: 'error',
                      titleText: 'Range Too Large',
                      content:
                        'Can not export calls with a date range greater than 3 months. Adjust filters and try again.'
                    });
                    return;
                  }
                }

                try {
                  setCsvLoading(true);

                  const companyData = await api.companies.unpagedListFull({
                    params: { accountId: phoneCallParams.accountId, serializer: 'full' }
                  });

                  const phoneCalls = await fetchAllPhoneCalls();

                  if (!phoneCalls.length) {
                    snackBar.enqueueSnackbar('No Phone Calls to Export');
                    return;
                  }

                  const csvString = generatePhoneCallsCsv({
                    phoneCalls,
                    timezone,
                    companies: companyData.results || [],
                    users: accessibleCallAgents
                  });

                  if (!csvString) {
                    snackBar.enqueueSnackbar('Error generating CSV');
                    return;
                  }

                  downloadCsv(csvString, 'call-log.csv');
                } catch (err) {
                  reportException(err);
                  snackBar.enqueueSnackbar('Error generating CSV');
                } finally {
                  setCsvLoading(false);
                }
              }}
              startIcon={<FileDownloadIcon />}
              sx={{ textTransform: 'none', marginLeft: 'auto' }}
            />
          </>
        }
      />
      <Divider />
      <GridV2
        container
        spacing={1}
        sx={(theme) => ({ backgroundColor: theme.palette.background.default, padding: 1.5 })}>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SelectFilter
            label='Call Outcome'
            initialKey={params.outcome}
            options={activeCallOutcomes()}
            enableEmptyValue
            onChange={(outcome) => setParam('outcome', outcome)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SearchSelect
            doSearch={async (searchValue) => {
              const res = await api.companies.list({ params: { searchWords: searchValue } });
              return res.results;
            }}
            selectedOption={companyQuery?.data || null}
            trySetSelectedOption={async (option) => {
              if (option) {
                setParams({ companyId: option.id, companyDNIS: null });
                return true;
              }
              setParams({ companyId: null, companyDNIS: null });
              return false;
            }}
            getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            renderListOption={(company, liProps) => {
              return (
                <li {...liProps} key={company.id}>
                  <Typography text={company.name} />
                </li>
              );
            }}
            label='Company'
            placeHolder='Company Name'
            sx={searchSelectSx}
          />
        </GridV2>
        {cachedAuthUserQuery.data &&
          (SystemUser({ user: cachedAuthUserQuery.data }) || CallCenterUser({ user: cachedAuthUserQuery.data })) && (
            <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <SearchSelect
                doSearch={async (searchValue) => {
                  const res = await api.accounts.unpagedListNameOnly({
                    params: { searchWords: searchValue, serializer: 'name' }
                  });
                  return res.results;
                }}
                selectedOption={accountQuery?.data || null}
                trySetSelectedOption={async (option) => {
                  if (option) {
                    setParam('accountId', option.id);
                    return true;
                  }
                  deleteParam('accountId');
                  return false;
                }}
                getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                renderListOption={(account, liProps) => {
                  return (
                    <li {...liProps} key={account.id}>
                      <Typography text={account.name} />
                    </li>
                  );
                }}
                label='Account'
                placeHolder='Account Name'
                sx={searchSelectSx}
              />
            </GridV2>
          )}
        {cachedAuthUserQuery.data &&
          (SystemUser({ user: cachedAuthUserQuery.data }) || CallCenterAdmin({ user: cachedAuthUserQuery.data })) && (
            <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <SelectFilter
                label='Agent Type'
                initialKey={sanitizedAgentType}
                options={agentTypeOptions}
                onChange={(agentType) => {
                  setParam('agentType', agentType);
                }}
                sx={{ width: '100%' }}
              />
            </GridV2>
          )}
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SelectFilter
            label='Company DNIS'
            initialKey={sanitizedCompanyDNIS}
            options={companyQuery.data?.callCenter.dnisArray.map((dnisItem) => dnisItem.phoneNumber) || []}
            enableEmptyValue
            usePhoneMask
            onChange={(companyDNIS) => setParam('companyDNIS', companyDNIS)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SearchSelect
            doSearch={async (searchValue) => {
              return accessibleCallAgents.filter((user) =>
                `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchValue.toLowerCase())
              );
            }}
            selectedOption={selectedCallAgent || null}
            trySetSelectedOption={async (option) => {
              if (option) {
                setParam('createdById', option.id);
                return true;
              }
              deleteParam('createdById');
              return false;
            }}
            getSelectedOptionLabel={(option) =>
              typeof option === 'string' ? option : `${option.firstName} ${option.lastName}`
            }
            renderListOption={(user, liProps) => {
              return (
                <li {...liProps} key={user.id}>
                  <Typography text={`${user.firstName} ${user.lastName}`} />
                </li>
              );
            }}
            label='Call Agent'
            placeHolder='Call Agent'
            sx={searchSelectSx}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <DebouncedTextField
            label='Caller ID'
            debounceOptions={{
              initialValue: params.callerId
            }}
            onChangeHandler={(text) => {
              if (text.length !== 10 && text.length !== 0) {
                setCallerIdIsPartiallyFilled(true);
                return;
              }
              setCallerIdIsPartiallyFilled(false);
              setParam('callerId', text);
            }}
            helperText={callerIdIsPartiallyFilled && 'Invalid Phone Ignored'}
            slotProps={{
              input: {
                inputComponent: MaskedPhoneInput,
                endAdornment: params.callerId && (
                  <IconButton
                    size='small'
                    onClick={() => {
                      setCallerIdIsPartiallyFilled(false);
                      deleteParam('callerId');
                    }}>
                    <CloseIcon />
                  </IconButton>
                )
              },
              formHelperText: {
                error: callerIdIsPartiallyFilled
              }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                paddingRight: 1
              }
            }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <Search
            placeholder='Search Customer Name...'
            initialValue={params.searchWords}
            millis={500}
            onSearch={(searchWords) => setParam('searchWords', searchWords)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                color='primary'
                checked={params.aiTransfer}
                onChange={(e) => setParam('aiTransfer', String(!!e.target.checked))}
              />
            }
            label={<Typography text='CPI Transfer' />}
          />
        </GridV2>
      </GridV2>
      <Divider />
      <Stack
        spacing={2}
        direction={singleColumn ? 'column' : 'row'}
        sx={(theme) => ({ backgroundColor: theme.palette.background.default, padding: 1.5 })}>
        <SelectFilter
          label='Date Range'
          initialKey={params.dateRangeFilter}
          options={dateRangeOptions.filter((option) => !(option === DateRangeEnum.YearToDate && isAccountUser))}
          onChange={(dateRangeOption) => {
            if (isDateRangeOption(dateRangeOption)) {
              if (dateRangeOption === DateRangeEnum.Today) {
                setParams({
                  createdAt_gte: createdAt_gte_Today,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.Today
                });
              } else if (dateRangeOption === DateRangeEnum.WeekToDate) {
                setParams({
                  createdAt_gte: createdAt_gte_Week,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.WeekToDate
                });
              } else if (dateRangeOption === DateRangeEnum.MonthToDate) {
                setParams({
                  createdAt_gte: createdAt_gte_Month,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.MonthToDate
                });
              } else if (dateRangeOption === DateRangeEnum.YearToDate) {
                setParams({
                  createdAt_gte: createdAt_gte_Year,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.YearToDate
                });
              }
            }
          }}
          sx={{ width: singleColumn ? '100%' : 'auto' }}
        />
        <DatePicker
          label='Start Date'
          timezone={timezone}
          value={params.createdAt_gte || createdAt_gte_Today}
          onChange={(value) => {
            if (!value) return;
            const startOfSelectedDay = DateUtils.toStartOf(value, 'day', { timezone }).toISOString();
            setParams({
              createdAt_gte: startOfSelectedDay,
              dateRangeFilter: dateRangeOptions[4]
            });
          }}
          textFieldProps={{
            fullWidth: false,
            sx: {
              width: singleColumn ? '100%' : 'auto'
            }
          }}
        />
        <DatePicker
          label='End Date'
          timezone={timezone}
          value={params.createdAt_lte || createdAt_lte_Today}
          onChange={(value) => {
            if (!value) return;
            const endOfSelectedDay = DateUtils.toEndOf(value, 'day', { timezone }).toISOString();
            setParams({
              createdAt_lte: endOfSelectedDay,
              dateRangeFilter: dateRangeOptions[4]
            });
          }}
          textFieldProps={{
            fullWidth: false,
            sx: {
              width: singleColumn ? '100%' : 'auto'
            }
          }}
        />
      </Stack>
      <Divider />
      <Stack
        direction='row-reverse'
        padding={1.5}
        sx={(theme) => ({ backgroundColor: theme.palette.background.default, width: '100%' })}>
        <Button
          color='cancel'
          variant='contained'
          text='Reset Filters'
          disabled={searchParams.toString().length === 0}
          onClick={() => clearParams()}
        />
      </Stack>
      <Divider />
      {phoneCallCountQuery.data !== undefined && !!transformListData(phoneCallQuery.data).length && (
        <>
          <Stack
            direction='row'
            justifyContent='center'
            spacing={1}
            padding={1}
            sx={(theme) => ({ backgroundColor: theme.palette.background.default })}>
            <Typography text={`Total calls: ${phoneCallCountQuery.data}`} variant='body1Bold' />
            <Typography
              text={`Appointments Booked: ${(() => {
                if (phoneCallBookedCountQueryEnabled) return phoneCallBookedCountQuery.data;
                else if (params.outcome === CallOutcomeEnum.NewAppointment) return phoneCallCountQuery.data;
                return 0;
              })()}`}
              variant='body1Bold'
            />
          </Stack>
          <Divider />
        </>
      )}
      <ScrollableContentWrapper
        isSavingOrLoading={
          phoneCallQuery.isFetching ||
          companyNameQuery.isFetching ||
          accountUsersQuery.isFetching ||
          callCenterUsersQuery.isFetching ||
          csvLoading
        }>
        <PagedList
          queryResult={phoneCallQuery}
          renderFunction={(phoneCall) => {
            const company = companyNameQuery.data?.find((company) => company.id === phoneCall.companyId);
            const user = accessibleCallAgents.find((accountUser) => accountUser.id === phoneCall.createdById);
            return (
              <ListItem
                key={phoneCall.id}
                divider
                disablePadding
                secondaryAction={
                  <Tooltip title='View in new tab' placement='left'>
                    <IconButton
                      onClick={() => {
                        window.open(`${getFullAppRoutePath(appRoutePaths.CallLog)}/${phoneCall.id}`, '_blank');
                      }}>
                      {<OpenInNewIcon />}
                    </IconButton>
                  </Tooltip>
                }>
                <LinkListItemButton
                  href={`${getFullAppRoutePath(appRoutePaths.CallLog)}/${phoneCall.id}`}
                  onClick={() => {
                    navigate(`${phoneCall.id}`, {
                      state: { goBack: getGoBackState(appRoutePaths.CallLog, searchParams) }
                    });
                  }}>
                  <ListItemAvatar>
                    <Avatar>
                      <PhoneIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack spacing={0}>
                        <Typography
                          text={`${phoneCall.customerDisplayName || 'Unknown Caller'} \u2192 ${company?.name || 'Inactive Company'}`}
                        />
                        <Typography
                          variant='body2'
                          text={!user ? 'Unknown Call Agent' : `${user.firstName} ${user.lastName}`}
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography component='span' variant='inherit' sx={{ display: 'flex', alignItems: 'center' }}>
                        {`${DateUtils.format(phoneCall.createdAt, DateFormatEnum.DateTimeWeekdayShort, {
                          timezone
                        })} \u2014 ${DateUtils.toPrettyDuration(phoneCall.duration)} \u2014 ${phoneCall.outcome || 'N/A'}`}
                        {!!phoneCall.aiCallId && (
                          <>
                            &nbsp;&mdash;&nbsp;
                            <Typography
                              component='span'
                              variant='inherit'
                              sx={(theme) => ({
                                color: theme.palette.infoSecondary.main,
                                display: 'flex',
                                flexDirection: 'row'
                              })}>
                              <AutoAwesomeIcon fontSize='small' /> CPI Transfer
                            </Typography>
                          </>
                        )}
                      </Typography>
                    }
                  />
                </LinkListItemButton>
              </ListItem>
            );
          }}
        />
      </ScrollableContentWrapper>
      {callCustomerDialog.element}
      {alertDialog.element}
    </FlexBox>
  );
};

export const PhoneCallListRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();

  const { callCenterCacheResource } = useContext(AuthContext);
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  return (
    <>
      {cachedCallCenterQuery.data?.timezone && (
        <PhoneCallList appDrawerState={appDrawerState} timezone={cachedCallCenterQuery.data.timezone} />
      )}
    </>
  );
};
